import { Injectable } from '@angular/core';
import { CustomModelAction, CustomModelProperty } from 'app/automations/automations-models';
import { Logger, LoggerFactory } from 'app/core/logger.service';
import { HttpRequestDecorator } from 'app/core/triggerbee/http.request.decorator';
import { TriggerbeeApi } from 'app/core/triggerbee/triggerbee-api.enum';
import { Observable, of } from 'rxjs';
import { map } from 'rxjs/operators';

@Injectable({ providedIn: 'root' })
export class DefaultFieldMappingsService {
  private readonly logger: Logger;

  constructor(
    loggerFactory: LoggerFactory,
    private http: HttpRequestDecorator,
  ) {
    this.logger = loggerFactory.get('DefaultFieldMappingsService');
  }

  // Auto-fills empty destination fields (`fieldName`) from the saved per-integration defaults.
  // Returns once applied (or immediately when there is nothing to fill) so callers can react —
  // e.g. recompute whether the app still has an active field mapping after the default lands.
  public setDefaultFieldMapping(action: CustomModelAction, properties: CustomModelProperty[]): Observable<void> {
    const emptyFields = action.properties.reduce((acc, currentItem, index, array) => {
      if (currentItem.name === 'fieldValue' && array[index + 1]?.name === 'fieldName') {
        const value = array[index + 1].value;
        if (currentItem.value && !value) {
          acc.push({
            key: currentItem.value,
            value: value,
          });
        }
      }
      return acc;
    }, []);

    if (!emptyFields.length) {
      return of(undefined);
    }
    this.logger.debug('emptyFields', emptyFields);
    const integration = action.nameAction;
    return this.getDefaultFieldMappingSettings(integration).pipe(
      map((defaultFieldMappings) => {
        if (defaultFieldMappings?.mappings) {
          this.logger.debug('defaultFieldMappings', defaultFieldMappings);
          for (const field of emptyFields) {
            const defaultFieldMapping = defaultFieldMappings.mappings[field.key];
            if (!field.value && defaultFieldMapping) {
              const propIndex = properties.findIndex((x) => x.value === field.key) + 1;
              properties[propIndex].value = defaultFieldMapping;
            }
          }
        }
      }),
    );
  }

  public saveDefaultFieldMapping(
    prop: CustomModelProperty,
    index,
    properties: CustomModelProperty[],
    integration: string,
  ) {
    if (index) {
      const propertyName = properties[index - 1].value;
      const propertyValue = prop.value;
      this.getDefaultFieldMappingSettings(integration).subscribe((defaultFieldMappings) => {
        if (!defaultFieldMappings) {
          defaultFieldMappings = {
            mappings: {},
          };
        }
        defaultFieldMappings.mappings[propertyName] = propertyValue;
        this.saveDefaultFieldMappingSettings(integration, defaultFieldMappings);
      });
    }
  }

  private getDefaultFieldMappingSettings(integration: string): Observable<DefaultFieldMappings> {
    return this.http.get(this.getUrl(integration));
  }

  private saveDefaultFieldMappingSettings(integration: string, defaultFieldMappings: DefaultFieldMappings) {
    return this.http.post(this.getUrl(integration), defaultFieldMappings).subscribe();
  }

  private getUrl(integration: string) {
    return `${this.http.getBaseUrl(TriggerbeeApi.Gateway)}/default-field-mappings/${integration}`;
  }
}

export interface DefaultFieldMappings {
  mappings: { [key: string]: string };
}
